import React, { useEffect, useRef, useState } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { css } from '@codemirror/lang-css';
import { vscodeLight } from '@uiw/codemirror-theme-vscode';
import './CSSEditor.css';

const CSSEditor = ({ value, onChange, onBlur, className, placeholder = "Enter CSS code...", minHeight = 100, defaultHeight = 200 }) => {
    const containerRef = useRef(null);
    const [isResizing, setIsResizing] = useState(false);
    const [height, setHeight] = useState(defaultHeight);

    const handleChange = (val) => {
        onChange && onChange(val);
    };

    const handleBlur = () => {
        onBlur && onBlur();
    };

    const handleMouseDown = (e) => {
        if (e.target.classList.contains('resize-handle')) {
            setIsResizing(true);
            e.preventDefault();
        }
    };

    const handleMouseMove = (e) => {
        if (isResizing && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const newHeight = e.clientY - rect.top;
            if (newHeight > minHeight) { // Minimum height
                setHeight(newHeight);
            }
        }
    };

    const handleMouseUp = () => {
        setIsResizing(false);
    };

    useEffect(() => {
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isResizing]);

    return (
        <div 
            ref={containerRef}
            className={`css-editor-container ${className || ''}`}
            onMouseDown={handleMouseDown}
        >
            <div className="editor-header">
                <span className="editor-title">CSS Editor</span>
                <div className="editor-controls">
                    <span className="height-display">{height}px</span>
                    <button 
                        className="reset-height-btn"
                        onClick={() => setHeight(defaultHeight)}
                        title="Reset to default height"
                    >
                        ↺
                    </button>
                </div>
            </div>
            <div style={{ height: `${height}px` }}>
                <CodeMirror
                    value={value || ''}
                    height={`${height}px`}
                    extensions={[css()]}
                    theme={vscodeLight}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    basicSetup={{
                        lineNumbers: true,
                        highlightActiveLineGutter: true,
                        highlightSpecialChars: true,
                        history: true,
                        foldGutter: true,
                        drawSelection: true,
                        dropCursor: true,
                        allowMultipleSelections: true,
                        indentOnInput: true,
                        syntaxHighlighting: true,
                        bracketMatching: true,
                        closeBrackets: true,
                        autocompletion: true,
                        rectangularSelection: true,
                        crosshairCursor: true,
                        highlightActiveLine: true,
                        highlightSelectionMatches: true,
                        closeBracketsKeymap: true,
                        defaultKeymap: true,
                        searchKeymap: true,
                        historyKeymap: true,
                        foldKeymap: true,
                        completionKeymap: true,
                        lintKeymap: true
                    }}
                />
            </div>
            <div className="resize-handle" />
        </div>
    );
};

export default CSSEditor; 